import {
  type IObservableArray,
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx'
import { type StaticModelCollection } from '../firestore-mobx/model'
import { getCatalogsForUser } from '../firestore/Catalog'
import { deleteTARecordFromAppUser, getUsers } from '../firestore/PublicUser'
import {
  SectionPaymentMethod,
  SectionState,
  createSection,
  deleteSection,
  fetchSection,
  getSectionsById,
  getSectionsStreamForInstructor,
  getSectionsStreamForInstructors,
  setSectionState,
  updateSection,
} from '../firestore/Section'
import { getSectionAssignments } from '../firestore/SectionAssignment'
import { getSectionPromotions } from '../firestore/SectionPromotion'
import { getTAInstructorIDs } from '../firestore/UserProfile'
import {
  applyApprovedFreeAdminPromotion,
  redeemPromotions,
} from '../firestore/UserPromotionRedemption'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { PublicUser } from '../models/PublicUser'
import { Section, SectionStatus } from '../models/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import type { SectionPromotion } from '../models/SectionPromotion'
import type { UserPromotion } from '../models/UserPromotion'
import type {
  ImpersonationInfo,
  InvitationType,
  SectionWithStatusAndAssignments,
} from '../types'
import { UserProfileRole, SectionRequestState } from '../types'
import { Cubit } from './core'
import { captureException } from '@sentry/core'
import { SectionRequest } from '../models/SectionRequest'
import type { FirestoreSectionRequest } from '../firestore/SectionRequest'
import {
  createSectionInvoiceRequest,
  getSectionRequestsBySectionId,
  SectionRequestType,
} from '../firestore/SectionRequest'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { dependentOn } from '../util/dependentOn'
import { DateTime } from 'luxon'
import { createStudentInvitation } from '../firestore/Invitation'
import { Organization } from '../models/Organization'
import { getOrganization } from '../firestore/Organization'

export type InstructorClassesSortingStrategy =
  | 'className'
  | 'sectionName'
  | 'students'
  | 'dates'
  | 'status'
  | 'instructor'

export type InstructorClassesSortingOrder = 'asc' | 'desc'

export class InstructorClassesCubit extends Cubit {
  repository: FirebaseRepository
  userId: string
  role: string

  @observable showCompleted = false
  @observable sortingStrategy: InstructorClassesSortingStrategy = 'status'
  @observable sortingOrder: InstructorClassesSortingOrder = 'asc'

  _sections: StaticModelCollection<Section>
  sharedSections: StaticModelCollection<Section>
  settingsSectionPassPricing: SettingsSectionPassPricing

  promotionsForSection = observable.map<string, SectionPromotion[]>()

  assignmentsForSection = observable.map<
    string,
    StaticModelCollection<SectionAssignment>
  >()

  invoiceRequestsBySectionId = observable.map<
    string,
    StaticModelCollection<SectionRequest>
  >()

  organizationsBySectionId = observable.map<string, Organization | null>()

  TAInstructors: StaticModelCollection<PublicUser>
  TAInstructorIds = observable.array<string>([])
  private _sharedSectionIds: IObservableArray<string> | undefined

  instructorUser: PublicUser
  impersonationInfo?: ImpersonationInfo

  fallbackSectionPassPrice: SettingsSectionPassPricing

  constructor(
    repository: FirebaseRepository,
    {
      defaultShowCompleted,
      role,
      impersonationInfo,
    }: {
      role: string
      defaultShowCompleted: boolean
      impersonationInfo?: ImpersonationInfo
    }
  ) {
    super()
    makeObservable(this)
    this.impersonationInfo = impersonationInfo

    this.userId = repository.uid
    this.repository = repository
    this.role = role

    this._sections = Section.emptyCollection(repository)
    this.sharedSections = Section.emptyCollection(repository)
    this.TAInstructors = PublicUser.emptyCollection(repository)
    this.instructorUser = PublicUser.empty(repository)
    this.settingsSectionPassPricing =
      SettingsSectionPassPricing.empty(repository)
    this.showCompleted = defaultShowCompleted
    this.fallbackSectionPassPrice = SettingsSectionPassPricing.empty(repository)
  }

  initialize(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const cubit = this

    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (settings) => {
        this.settingsSectionPassPricing.replaceModel(settings)
      },
      { name: 'settings-section-pass-pricing' }
    )

    if (this.role === 'ta') {
      this.addStream(getTAInstructorIDs(this.repository), (instructorIds) => {
        runInAction(() => {
          this.TAInstructorIds.replace(instructorIds)
        })
      })
      // when instructor IDs updates re-init the instructor profile and sections streams
      this.addReaction({
        whenThisChanges: () => this.TAInstructorIds.length,
        thenRunThisCode: () => {
          const sectionsStreamLabel = 'ta-sections-stream'
          const instructorsStreamLabel = 'ta-instructors-stream'
          this.removeStream(sectionsStreamLabel)
          this.removeStream(instructorsStreamLabel)
          this.addStream(
            getSectionsStreamForInstructors(
              this.repository,
              this.TAInstructorIds
            ),
            (sections) => {
              this._sections.replaceModels(sections)
              this.sharedSections.replaceModels([])
              this.addStreamsForSections()
            },
            { name: sectionsStreamLabel }
          )
          cubit.addStream(
            getUsers(cubit.repository, { userIds: cubit.TAInstructorIds }),
            (instructors) => cubit.TAInstructors.replaceModels(instructors)
          )
        },
      })
      return
    }

    this.addStream(
      getSectionsStreamForInstructor(this.repository, this.impersonationInfo),
      (sections) => {
        this._sections.replaceModels(sections)
        this.startSectionPromotionStreams(sections)
        this.addStreamsForSections()

        const fromCache = sections.every((section) => section.doc.fromCache)
        const hasUncompletedSections = sections.some(
          (section) => section.data.sectionState !== SectionState.completed
        )

        // if there is a single section which we close on the instructor class page
        // we will only receive a cache result, not a server result when we navigate
        // back to classes page.
        const singleSectionFromCache = fromCache && sections.length === 1

        // When all sections are completed, default to show completed sections.
        if (!hasUncompletedSections && !singleSectionFromCache) {
          this.showCompleted = true
        }
      },
      { name: 'sections' }
    )

    this.addStream(
      getCatalogsForUser(this.repository, {
        userId: this.impersonationInfo?.userId ?? this.userId,
      }),
      (catalogs) => {
        const sharedSectionStreamKey = 'shared-sections-stream'
        const sharedSectionIds = Array.from(
          new Set(
            catalogs.flatMap(
              ({ catalog }) => catalog.data.catalogSharedSectionIds
            )
          )
        )
        if (
          this._sharedSectionIds &&
          this.arraysEqual(sharedSectionIds, this._sharedSectionIds)
        ) {
          return
        }
        this._sharedSectionIds = observable.array(sharedSectionIds)
        this.removeStream(sharedSectionStreamKey)
        this.addStream(
          getSectionsById(this.repository, sharedSectionIds),
          (sharedSections) => {
            // iterate over shared sections and get assignments stream
            sharedSections.forEach((section) => {
              const sharedSectionStreamKey = `shared-section-assignments-${section.id}`
              if (this.hasStream(sharedSectionStreamKey)) return
              this.addStreamsForSection(section, sharedSectionStreamKey)
            })
            this.sharedSections.replaceModels(sharedSections)
          },
          {
            name: sharedSectionStreamKey,
          }
        )
      },
      { name: 'catalogs' }
    )

    // if we are impersonating an instructor, get the instructor's profile
    if (this.impersonationInfo?.userId) {
      this.addStream(
        getUsers(this.repository, {
          userIds: [this.impersonationInfo?.userId],
        }),
        (instructors) => {
          this.instructorUser.replaceModel(instructors[0])
          return
        },
        { name: 'instructor-profile' }
      )
    }

    this.addStream(getSettingsSectionPassPricing(this.repository), (pricing) =>
      this.fallbackSectionPassPrice.replaceModel(
        pricing.hasData
          ? pricing
          : SettingsSectionPassPricing.empty(this.repository)
      )
    )
  }

  addStreamsForSections() {
    this._sections.models.forEach((section) => {
      this.addStreamsForSection(section)
    })
  }

  @action
  addStreamsForSection(section: Section, name?: string) {
    this.assignmentsForSection.set(
      section.id,
      SectionAssignment.emptyCollection(this.repository)
    )
    this.invoiceRequestsBySectionId.set(
      section.id,
      SectionRequest.emptyCollection(this.repository)
    )
    this.organizationsBySectionId.set(
      section.id,
      Organization.empty(this.repository)
    )
    this.startAssignmentStream(section, 0, name)
    if (section.data.organizationId) {
      this.startSectionRequestStream(section, 0, name)
      this.startOrganizationStream(section)
    }
  }

  @action
  startAssignmentStream(section: Section, retryCount = 0, name?: string) {
    this.addStream(
      getSectionAssignments(this.repository, { sectionId: section.id }),
      (assignments) => {
        this.assignmentsForSection.get(section.id)?.replaceModels(assignments)
      },
      {
        name: name ?? `section-assignments-${section.id}`,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startAssignmentStream(section, retryCount + 1, name)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  @action
  startSectionRequestStream(section: Section, retryCount = 0, name?: string) {
    this.addStream(
      getSectionRequestsBySectionId(this.repository, section.id),
      (requests) => {
        const sortedRequests = requests.sort((a, b) => {
          return b.data.requestedAt.getTime() - a.data.requestedAt.getTime()
        })

        const invoiceRequests = sortedRequests.filter(
          (request) =>
            request.data.sectionRequestType === SectionRequestType.invoice
        )

        this.invoiceRequestsBySectionId
          .get(section.id)!
          .replaceModels(invoiceRequests)
      },
      {
        name: name ?? `section-requests-${section.id}`,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startSectionRequestStream(section, retryCount + 1, name)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  @action
  startOrganizationStream(section: Section) {
    this.addStream(
      getOrganization(this.repository, {
        organizationId: section.data.organizationId!,
      }),
      (organization) => {
        this.organizationsBySectionId.set(section.id, organization)
      },
      {
        name: `organization-${section.id}`,
        disableCaptureException: true,
        onError: (error) => {
          captureException(error)
        },
      }
    )
  }

  /** @deprecated */
  isSharedSection = (section: Section) => {
    return this._sharedSectionIds?.includes(section.id) ?? false
  }

  @action
  toggleShowCompleted() {
    this.showCompleted = !this.showCompleted
  }

  fetchSection(sectionId: string) {
    return fetchSection(this.repository, {
      sectionId,
    })
  }

  @computed
  get sections() {
    return dependentOn(
      !(
        this._sections.isLoading ||
        (this.role === 'ta' && this.TAInstructors.isLoading)
      ),
      () => {
        return this._sections.models
      }
    )
  }

  @computed
  get sampleSections() {
    return dependentOn(!this.sharedSections.isLoading, () => {
      return this.sharedSections.models
    })
  }

  @computed
  get sectionsByInstructor() {
    return dependentOn(!this.sections.isLoading, () => {
      const sectionsByInstructorId: Record<
        string,
        { instructor: PublicUser; sections: Section[] }
      > = {}
      const instructorMap = Object.fromEntries(
        this.TAInstructors.models.map((instructor) => [
          instructor.id,
          instructor,
        ])
      )
      for (const instructor of this.TAInstructors.models) {
        sectionsByInstructorId[instructor.id] = { instructor, sections: [] }
      }
      this.sections.value!.forEach((section) => {
        const instructorId = section.data.instructorUserId
        if (!sectionsByInstructorId[instructorId]) {
          const instructor =
            instructorId in instructorMap
              ? instructorMap[instructorId]
              : PublicUser.empty(this.repository)
          sectionsByInstructorId[instructorId] = { instructor, sections: [] }
        }
        sectionsByInstructorId[instructorId].sections.push(section)
      })
      return sectionsByInstructorId
    })
  }

  @computed
  get sectionsWithStatusAndAssignments() {
    return dependentOn(
      !this.sections.isLoading &&
        this.sections.value.every(
          (section) => this.assignmentsForSection.get(section.id)?.isLoaded
        ) &&
        this.sections.value.every(
          (section) =>
            !section.data.organizationId ||
            this.invoiceRequestsBySectionId.get(section.id)?.isLoaded
        ),
      () => {
        const sections: SectionWithStatusAndAssignments[] =
          this.sections.value!.map((section) => {
            const assignments = this.assignmentsForSection.get(
              section.id
            )!.models

            const latestInvoiceRequest = this.invoiceRequestsBySectionId.get(
              section.id
            )!.models[0]

            const sectionStatus = section.getSectionStatus({
              assignments,
              latestInvoiceRequest,
            })

            return {
              section,
              sectionStatus,
              assignments,
            }
          })

        sections.sort(
          (
            a: SectionWithStatusAndAssignments,
            b: SectionWithStatusAndAssignments
          ) => {
            let comparison = 0
            switch (this.sortingStrategy) {
              case 'className':
                comparison = a.section.data.className.localeCompare(
                  b.section.data.className
                )
                break
              case 'sectionName':
                comparison = a.section.data.sectionName.localeCompare(
                  b.section.data.sectionName
                )
                break
              case 'students':
                comparison = a.section.userCount - b.section.userCount
                break
              case 'dates': {
                const aDate =
                  a.section.data.sectionStartDate ??
                  a.section.data.sectionEndDate
                const bDate =
                  b.section.data.sectionStartDate ??
                  b.section.data.sectionEndDate

                if (!aDate && !bDate) {
                  comparison = 0
                } else if (!aDate) {
                  comparison = -1
                } else if (!bDate) {
                  comparison = 1
                } else {
                  const aStart = DateTime.fromJSDate(aDate).startOf('day')
                  const bStart = DateTime.fromJSDate(bDate).startOf('day')
                  comparison = aStart.diff(bStart).milliseconds
                }
                break
              }
              case 'status': {
                const statusOrder = {
                  [SectionStatus.draft]: 0,
                  [SectionStatus.invoiceRejected]: 1,
                  [SectionStatus.invoicePending]: 2,
                  [SectionStatus.notStarted]: 3,
                  [SectionStatus.inProgress]: 4,
                  [SectionStatus.completed]: 5,
                }
                comparison =
                  statusOrder[a.sectionStatus] - statusOrder[b.sectionStatus]
                break
              }
              case 'instructor': {
                const instructorA = this.TAInstructors.models.find(
                  (i) => i.id === a.section.data.instructorUserId
                )
                const instructorB = this.TAInstructors.models.find(
                  (i) => i.id === b.section.data.instructorUserId
                )
                const nameA = instructorA
                  ? `${instructorA.data.firstName} ${instructorA.data.lastName}`
                  : ''
                const nameB = instructorB
                  ? `${instructorB.data.firstName} ${instructorB.data.lastName}`
                  : ''
                comparison = nameA.localeCompare(nameB)
                break
              }
            }
            return this.sortingOrder === 'asc' ? comparison : -comparison
          }
        )

        return sections
      }
    )
  }

  @computed
  get allowSectionPassPaymentMethod() {
    return (
      this.settingsSectionPassPricing.isLoaded &&
      this.settingsSectionPassPricing.hasData
    )
  }

  invoiceRequestsForSectionId(sectionId: string) {
    return dependentOn(
      this._sections.isLoaded &&
        this._sections.models.some(
          (section) =>
            !section.data.organizationId ||
            this.invoiceRequestsBySectionId.get(section.id)?.isLoaded
        ),
      () => {
        return this.invoiceRequestsBySectionId.get(sectionId)!
      }
    )
  }

  organizationForSectionId(sectionId: string) {
    return dependentOn(
      this._sections.isLoaded &&
        this._sections.models.some(
          (section) =>
            section.id === sectionId &&
            (!section.data.organizationId ||
              this.organizationsBySectionId.get(section.id)?.isLoaded)
        ),
      () => {
        return this.organizationsBySectionId.get(sectionId)
      }
    )
  }

  createSectionInvoiceRequest = async ({
    sectionId,
    sectionRequest,
  }: {
    sectionId: string
    sectionRequest: Pick<
      FirestoreSectionRequest,
      | 'sectionRequestAssignmentCount'
      | 'sectionRequestStudentCount'
      | 'requestedAt'
      | 'organizationId'
      | 'sectionId'
      | 'updatedByUserId'
    >
  }) => {
    await createSectionInvoiceRequest(this.repository, sectionId, {
      ...sectionRequest,
      userId: this.repository.uid,
      sectionRequestType: SectionRequestType.invoice,
      sectionRequestState: SectionRequestState.pending,
      updatedAt: new Date(),
      requestedAt: new Date(),
    })
  }

  createSection = async ({
    className,
    sectionName,
    organizationId,
    impersonationInfo,
    sectionPaymentMethod,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
  }: {
    className: string
    sectionName: string
    organizationId?: string
    impersonationInfo?: ImpersonationInfo
    sectionPaymentMethod?: SectionPaymentMethod
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
  }) => {
    if (this.role === UserProfileRole.ta && !impersonationInfo?.userId) {
      throw new Error('instructorUserId is required for TA to create a section')
    }
    const doc = await createSection(this.repository, {
      className,
      sectionName,
      userId: impersonationInfo?.userId ?? this.userId,
      organizationId,
      sectionPaymentMethod: sectionPaymentMethod ?? SectionPaymentMethod.token,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
    })

    return doc.id
  }

  redeemPromotions = async (
    sectionId: string,
    userPromotions: UserPromotion[]
  ) => {
    await Promise.all(
      userPromotions.map((userPromotion) =>
        redeemPromotions(this.repository, {
          userId: userPromotion.data.userId,
          promotionId: userPromotion.data.promotionId,
          userPromotionId: userPromotion.id,
          sectionId,
        })
      )
    )
    return
  }

  applyApprovedFreeAdminPromotion = async ({
    userId,
    sectionId,
  }: {
    sectionId: string
    userId: string
  }) => {
    return await applyApprovedFreeAdminPromotion(this.repository, {
      userId,
      sectionId,
    })
  }

  retireAsTA(instructorUserId: string) {
    return deleteTARecordFromAppUser(this.repository.firestore, {
      userId: this.userId,
      instructorUserId,
    })
  }

  private arraysEqual(arr1: unknown[], arr2: unknown[]) {
    if (arr1.length !== arr2.length) return false
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false
    }
    return true
  }

  startSectionPromotionStreams(sections: Section[]): void {
    for (const section of sections) {
      this.startSectionPromotionStream(section)
    }
  }

  startSectionPromotionStream(section: Section, retryCount = 0): void {
    const name = `section-promotions-${section.id}`
    if (this.hasStream(name)) return
    this.addStream(
      getSectionPromotions(this.repository, {
        sectionId: section.id,
      }),
      (promotions) => {
        this.promotionsForSection.set(section.id, promotions)
      },
      {
        name: name,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startSectionPromotionStream(section, retryCount + 1)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  changeSortingStrategy(
    strategy: InstructorClassesSortingStrategy,
    order: InstructorClassesSortingOrder
  ) {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  async deleteSection(sectionId: string) {
    await deleteSection(this.repository, sectionId)
  }

  async closeSection(sectionId: string) {
    await setSectionState(this.repository, {
      sectionId,
      sectionState: SectionState.completed,
    })
  }

  updateSection =
    (sectionId: string) =>
    async ({
      className,
      sectionName,
      organizationId,
      sectionPaymentMethod,
      sectionPassPrice,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
    }: {
      className?: string
      sectionName?: string
      organizationId?: string
      sectionPaymentMethod?: SectionPaymentMethod
      sectionPassPrice?: number
      expectedStudents?: number
      sectionStartDate?: DateTime
      sectionEndDate?: DateTime
    }) => {
      await updateSection(this.repository, sectionId, {
        className,
        sectionName,
        organizationId,
        sectionPaymentMethod,
        sectionPassPrice,
        expectedStudents,
        sectionStartDate,
        sectionEndDate,
      })

      return sectionId
    }

  createStudentInvitation =
    (sectionId: string) =>
    async ({ type }: { type: InvitationType }) => {
      const doc = await createStudentInvitation(this.repository, {
        sectionId,
        type,
      })

      return doc.id
    }
}
